import { Injectable, DestroyRef, inject, signal, computed } from '@angular/core';
import { takeUntilDestroyed } from '@angular/core/rxjs-interop';
import { DOCUMENT } from '@angular/common';
import { fromEvent, debounceTime, startWith } from 'rxjs';

export interface Breakpoints {
  xs: boolean; // < 640px
  sm: boolean; // >= 640px
  md: boolean; // >= 768px
  lg: boolean; // >= 1024px
  xl: boolean; // >= 1280px
  xxl: boolean; // >= 1536px
}

@Injectable({
  providedIn: 'root'
})
export class ResponsiveService {
  private readonly destroyRef = inject(DestroyRef);
  private readonly document = inject(DOCUMENT);
  private readonly win = this.document.defaultView!;

  // Current window dimensions
  private windowWidth = signal(this.win.innerWidth);
  private windowHeight = signal(this.win.innerHeight);

  // Device detection
  readonly isMobile = computed(() => this.windowWidth() < 768);
  readonly isTablet = computed(() => this.windowWidth() >= 768 && this.windowWidth() < 1024);
  readonly isDesktop = computed(() => this.windowWidth() >= 1024);

  // Breakpoint signals
  readonly breakpoints = computed<Breakpoints>(() => {
    const width = this.windowWidth();
    return {
      xs: width < 640,
      sm: width >= 640,
      md: width >= 768,
      lg: width >= 1024,
      xl: width >= 1280,
      xxl: width >= 1536
    };
  });

  // Current breakpoint
  readonly currentBreakpoint = computed(() => {
    const bp = this.breakpoints();
    if (bp.xxl) {return 'xxl';}
    if (bp.xl) {return 'xl';}
    if (bp.lg) {return 'lg';}
    if (bp.md) {return 'md';}
    if (bp.sm) {return 'sm';}
    return 'xs';
  });

  // Orientation
  readonly isPortrait = computed(() => this.windowHeight() > this.windowWidth());
  readonly isLandscape = computed(() => this.windowWidth() > this.windowHeight());

  // Touch device detection
  private readonly _isTouchDevice = signal(this.detectTouchDevice());
  readonly isTouchDevice = this._isTouchDevice.asReadonly();

  // Platform detection
  private readonly _platform = signal(this.detectPlatform());
  readonly platform = this._platform.asReadonly();

  constructor() {
    // Set initial mobile viewport height CSS variable
    this.setViewportHeight();

    // Listen to window resize
    fromEvent(this.win, 'resize')
      .pipe(debounceTime(100), startWith(null), takeUntilDestroyed(this.destroyRef))
      .subscribe(() => {
        this.windowWidth.set(this.win.innerWidth);
        this.windowHeight.set(this.win.innerHeight);
        this.setViewportHeight();
      });

    // Listen to orientation change
    if ('orientationchange' in this.win) {
      fromEvent(this.win, 'orientationchange').pipe(takeUntilDestroyed(this.destroyRef)).subscribe(() => {
        setTimeout(() => {
          this.windowWidth.set(this.win.innerWidth);
          this.windowHeight.set(this.win.innerHeight);
          this.setViewportHeight();
        }, 100);
      });
    }

    // Detect touch capability changes (for hybrid devices)
    fromEvent(this.win, 'touchstart', { passive: true, once: true }).pipe(takeUntilDestroyed(this.destroyRef)).subscribe(() => {
      this._isTouchDevice.set(true);
    });
  }

  // Helper methods
  isBreakpoint(breakpoint: keyof Breakpoints): boolean {
    return this.breakpoints()[breakpoint];
  }

  isMinWidth(width: number): boolean {
    return this.windowWidth() >= width;
  }

  isMaxWidth(width: number): boolean {
    return this.windowWidth() <= width;
  }

  getWindowDimensions(): { width: number; height: number } {
    return {
      width: this.windowWidth(),
      height: this.windowHeight()
    };
  }

  // Mobile-specific helpers
  getViewportHeight(): number {
    // Account for mobile browser chrome
    return this.win.innerHeight || this.document.documentElement.clientHeight;
  }

  setViewportHeight(): void {
    // Set CSS custom property for true viewport height (useful for mobile)
    const vh = this.getViewportHeight() * 0.01;
    this.document.documentElement.style.setProperty('--vh', `${vh}px`);
  }

  // Private helper methods
  private detectTouchDevice(): boolean {
    const nav = this.win.navigator as Navigator & { msMaxTouchPoints?: number };
    return (
      'ontouchstart' in this.win ||
      this.win.navigator.maxTouchPoints > 0 ||
      (nav.msMaxTouchPoints ?? 0) > 0
    );
  }

  private detectPlatform(): string {
    const winExt = this.win as Window & { opera?: string; MSStream?: unknown };
    const userAgent = this.win.navigator.userAgent || this.win.navigator.vendor || winExt.opera || '';

    // iOS detection
    if (/iPad|iPhone|iPod/.test(userAgent) && !winExt.MSStream) {
      return 'ios';
    }

    // Android detection
    if (/android/i.test(userAgent)) {
      return 'android';
    }

    // Windows Mobile detection
    if (/windows phone/i.test(userAgent)) {
      return 'windows-mobile';
    }

    // Desktop detection
    if (/Mac/i.test(userAgent)) {
      return 'macos';
    }

    if (/Win/i.test(userAgent)) {
      return 'windows';
    }

    if (/Linux/i.test(userAgent)) {
      return 'linux';
    }

    return 'unknown';
  }

  // Utility method for responsive images
  getResponsiveImageSize(): string {
    const bp = this.currentBreakpoint();
    const sizes: Record<string, string> = {
      xs: 'small',
      sm: 'small',
      md: 'medium',
      lg: 'large',
      xl: 'large',
      xxl: 'xlarge'
    };
    return sizes[bp] || 'medium';
  }

  // Utility method for responsive column count
  getResponsiveColumns(): number {
    const bp = this.currentBreakpoint();
    const columns: Record<string, number> = {
      xs: 1,
      sm: 2,
      md: 3,
      lg: 4,
      xl: 5,
      xxl: 6
    };
    return columns[bp] || 3;
  }

  // Safe area insets for mobile devices
  getSafeAreaInsets(): { top: number; bottom: number; left: number; right: number } {
    const styles = this.win.getComputedStyle(this.document.documentElement);
    return {
      top: parseInt(styles.getPropertyValue('--safe-area-inset-top') || '0', 10),
      bottom: parseInt(styles.getPropertyValue('--safe-area-inset-bottom') || '0', 10),
      left: parseInt(styles.getPropertyValue('--safe-area-inset-left') || '0', 10),
      right: parseInt(styles.getPropertyValue('--safe-area-inset-right') || '0', 10)
    };
  }
}
